import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pm, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

/* ── Palette ───────────────────────────────────────────────── */
const STATUS = {
  planned:   { label: 'Planned',   dot: '#6366F1', bg: '#EEF2FF', text: '#4338CA' },
  active:    { label: 'Active',    dot: '#10B981', bg: '#ECFDF5', text: '#065F46' },
  on_hold:   { label: 'On Hold',   dot: '#F59E0B', bg: '#FFFBEB', text: '#92400E' },
  completed: { label: 'Completed', dot: '#6B7280', bg: '#F3F4F6', text: '#374151' },
  cancelled: { label: 'Cancelled', dot: '#F43F5E', bg: '#FFF1F2', text: '#9F1239' },
};

const MODE = {
  kanban: { label: 'Kanban', bg: '#DBEAFE', text: '#1D4ED8', icon: '▦' },
  scrum:  { label: 'Scrum',  bg: '#EDE9FE', text: '#6D28D9', icon: '⟳' },
  sprint: { label: 'Scrum',  bg: '#EDE9FE', text: '#6D28D9', icon: '⟳' },
};

const ACCENTS = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#10B981','#EF4444'];
const accent  = (name = '') => ACCENTS[name.charCodeAt(0) % ACCENTS.length];

const ADMIN_ROLES = ['super_admin', 'hr_admin', 'hr_head', 'ceo', 'project_manager', 'team_lead', 'project_lead'];

/* ── On-track logic (based on current milestone dates) ─────── */
function trackStatus(p) {
  if (p.status === 'completed') return { label: 'Completed',   color: '#059669', bg: '#D1FAE5', border: '#34D399', icon: '✓' };
  if (p.status === 'cancelled') return { label: 'Cancelled',   color: '#DC2626', bg: '#FEE2E2', border: '#FCA5A5', icon: '✕' };
  if (p.status === 'on_hold')   return { label: 'On Hold',     color: '#B45309', bg: '#FEF3C7', border: '#FDE68A', icon: '⏸' };
  if (p.status === 'planned')   return { label: 'Not Started', color: '#4F46E5', bg: '#EEF2FF', border: '#A5B4FC', icon: '◎' };

  // active — use current milestone dates if available
  const today  = new Date(); today.setHours(0, 0, 0, 0);
  const msDue  = p.current_milestone_due_date   ? new Date(p.current_milestone_due_date)   : null;
  const msStart= p.current_milestone_start_date ? new Date(p.current_milestone_start_date) : null;
  const msPct  = p.current_milestone_pct || 0;

  if (msDue) {
    const daysLeft = Math.ceil((msDue - today) / 86400000);

    // Milestone due date already passed and not 100% done → Delayed
    if (daysLeft < 0 && msPct < 100)
      return { label: 'Delayed', color: '#DC2626', bg: '#FEF2F2', border: '#F87171', icon: '▼' };

    // Compute time-based expected progress within milestone
    if (msStart && msDue > msStart) {
      const totalDays   = Math.ceil((msDue - msStart) / 86400000);
      const elapsed     = Math.max(0, Math.ceil((today - msStart) / 86400000));
      const expectedPct = Math.min(100, Math.round(elapsed / totalDays * 100));
      const gap         = expectedPct - msPct;

      if (gap > 25) return { label: 'Behind',   color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', icon: '▼' };
      if (gap > 10 || daysLeft <= 7)
                    return { label: 'At Risk',   color: '#D97706', bg: '#FFFBEB', border: '#FCD34D', icon: '⚡' };
      return              { label: 'On Track',   color: '#059669', bg: '#F0FDF4', border: '#6EE7B7', icon: '↑' };
    }

    // No start date — just use days-left proximity
    if (daysLeft <= 3  && msPct < 80) return { label: 'At Risk', color: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', icon: '⚡' };
    if (daysLeft <= 10 && msPct < 50) return { label: 'At Risk', color: '#D97706', bg: '#FFFBEB', border: '#FCD34D', icon: '⚡' };
    return { label: 'On Track', color: '#059669', bg: '#F0FDF4', border: '#6EE7B7', icon: '↑' };
  }

  // No current milestone — fall back to project end date
  const projEnd = p.end_date ? new Date(p.end_date) : null;
  if (projEnd) {
    const daysLeft = Math.ceil((projEnd - today) / 86400000);
    if (daysLeft < 0) return { label: 'Delayed', color: '#DC2626', bg: '#FEE2E2', border: '#F87171', icon: '▼' };
    if (daysLeft <= 14) return { label: 'At Risk', color: '#D97706', bg: '#FFFBEB', border: '#FCD34D', icon: '⚡' };
  }
  return { label: 'Active', color: '#2563EB', bg: '#EFF6FF', border: '#93C5FD', icon: '▶' };
}

/* ── Helpers ────────────────────────────────────────────────── */
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function ProgressBar({ pct, color, trackered = false }) {
  const bg    = trackered ? `${color}22` : '#E5E7EB';
  const glow  = pct > 0 ? `0 0 6px ${color}55` : 'none';
  return (
    <div style={{ width: '100%', background: bg, borderRadius: 999, height: 7, overflow: 'hidden' }}>
      <div style={{
        width: `${Math.min(100, pct)}%`, height: '100%', borderRadius: 999,
        background: `linear-gradient(90deg, ${color}cc, ${color})`,
        boxShadow: glow, transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

const inp = {
  padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`,
  borderRadius: 9, fontSize: '0.875rem', outline: 'none', background: '#fff',
};

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 600, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 90px rgba(0,0,0,0.22)', overflow: 'hidden' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#F8FAFF,#F0F4FF)', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: theme.text }}>{title}</h2>
          <button onClick={onClose} style={{ background: '#E5E7EB', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: '1rem', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Component ─────────────────────────────────────────────── */
export default function ProjectList() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const [projects, setProjects]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState({ name:'', code:'', description:'', mode:'kanban', status:'planned', start_date:'', end_date:'', total_value:'', payment_percentage:'' });
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving]       = useState(false);

  const EMPTY_MS = { name:'', planned_start_date:'', planned_end_date:'', payment_percentage:'' };

  const addMilestone = () => setMilestones(ms => [...ms, { ...EMPTY_MS }]);
  const removeMilestone = (i) => setMilestones(ms => ms.filter((_, idx) => idx !== i));
  const updateMilestone = (i, field, val) =>
    setMilestones(ms => ms.map((m, idx) => idx === i ? { ...m, [field]: val } : m));

  const totalPct = milestones.reduce((sum, m) => sum + (parseFloat(m.payment_percentage) || 0), 0);
  const [sortCol, setSortCol]     = useState('name');
  const [sortDir, setSortDir]     = useState('asc');

  const canCreate = ADMIN_ROLES.includes(user?.role);
  const canDelete = ADMIN_ROLES.includes(user?.role);

  const handleDelete = async (e, projectId, projectName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete project "${projectName}"? This will permanently remove all issues, milestones, and data. This cannot be undone.`)) return;
    try {
      await pm.deleteProject(projectId);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete project.');
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    pm.getProjects(statusFilter ? { status: statusFilter } : {})
      .then(r => { setProjects(r.data || []); setError(''); })
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (milestones.length === 0) { alert('Please add at least one milestone before creating the project.'); return; }
    if (Math.round(totalPct * 10) !== 1000) {
      const gap = parseFloat((100 - totalPct).toFixed(2));
      const gapAmt = form.total_value ? (parseFloat(form.total_value) * Math.abs(gap) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : null;
      if (totalPct > 100) {
        alert(`Total milestone payment is ${totalPct.toFixed(1)}% — exceeds 100% by ${Math.abs(gap)}%. Please reduce payment percentages.`);
      } else {
        alert(`Total milestone payment is ${totalPct.toFixed(1)}%. ${gap}%${gapAmt ? ` (₹${gapAmt})` : ''} is not mapped to any milestone. Allocate all 100% before creating the project.`);
      }
      return;
    }
    setSaving(true);
    try {
      const res = await pm.createProject(form);
      const projectId = res.data.id;
      // Create milestones sequentially to preserve M-1, M-2… ordering
      for (const ms of milestones) {
        if (!ms.name.trim() && !ms.planned_start_date && !ms.planned_end_date && !ms.payment_percentage) continue;
        await pm.createMilestone(projectId, {
          name:               ms.name.trim() || `Milestone ${milestones.indexOf(ms) + 1}`,
          planned_start_date: ms.planned_start_date || null,
          planned_end_date:   ms.planned_end_date   || null,
          payment_percentage: ms.payment_percentage ? parseFloat(ms.payment_percentage) : null,
        });
      }
      setShowModal(false);
      setForm({ name:'', code:'', description:'', mode:'kanban', status:'planned', start_date:'', end_date:'', total_value:'', payment_percentage:'', timeline_notes:'' });
      setMilestones([]);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create project.');
    } finally { setSaving(false); }
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = projects
    .filter(p =>
      (!statusFilter || p.status === statusFilter) &&
      (p.name.toLowerCase().includes(search.toLowerCase()) ||
       (p.code || '').toLowerCase().includes(search.toLowerCase()) ||
       (p.description || '').toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const counts = Object.fromEntries(Object.keys(STATUS).map(k => [k, projects.filter(p => p.status === k).length]));

  const SortIcon = ({ col }) =>
    sortCol === col
      ? <span style={{ color: theme.primary, marginLeft: 4, fontSize: '0.7rem' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
      : <span style={{ color: '#D1D5DB', marginLeft: 4, fontSize: '0.7rem' }}>⇅</span>;

  /* Column header groups */
  const thBase = { padding: '0.7rem 0.875rem', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase',
                   letterSpacing: '0.07em', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                   borderBottom: '2px solid rgba(0,0,0,0.08)', position: 'sticky', top: 0, zIndex: 1 };

  const Th = ({ col, label, minW = 90, bg = '#F1F5F9', color = '#4B5563', center = false }) => (
    <th onClick={() => toggleSort(col)} style={{ ...thBase, minWidth: minW, textAlign: center ? 'center' : 'left', background: bg, color }}>
      {label}<SortIcon col={col} />
    </th>
  );

  /* Summary stats */
  const onTrackCount  = projects.filter(p => ['On Track'].includes(trackStatus(p).label)).length;
  const atRiskCount   = projects.filter(p => ['At Risk','Behind'].includes(trackStatus(p).label)).length;
  const delayedCount  = projects.filter(p => trackStatus(p).label === 'Delayed').length;

  return (
    <div style={{ maxWidth: 1500 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: theme.text }}>Projects</h1>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.875rem', color: theme.textMuted }}>Track progress and health across all projects</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {/* Mini health summary */}
          {!loading && projects.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span style={{ padding: '0.3rem 0.75rem', borderRadius: 9999, background: '#D1FAE5', color: '#065F46', fontSize: '0.75rem', fontWeight: 700 }}>↑ {onTrackCount} On Track</span>
              {atRiskCount > 0  && <span style={{ padding: '0.3rem 0.75rem', borderRadius: 9999, background: '#FEF3C7', color: '#92400E', fontSize: '0.75rem', fontWeight: 700 }}>⚡ {atRiskCount} At Risk</span>}
              {delayedCount > 0 && <span style={{ padding: '0.3rem 0.75rem', borderRadius: 9999, background: '#FEE2E2', color: '#991B1B', fontSize: '0.75rem', fontWeight: 700 }}>▼ {delayedCount} Delayed</span>}
            </div>
          )}
          {canCreate && (
            <button onClick={() => setShowModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: `linear-gradient(135deg,${theme.primary},#6366F1)`, color: '#fff', border: 'none', borderRadius: 10, padding: '0.6rem 1.25rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem', boxShadow: `0 4px 16px ${theme.primary}55` }}>
              + New Project
            </button>
          )}
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setStatusFilter('')}
          style={{ padding: '0.35rem 0.875rem', borderRadius: 9999, border: `1.5px solid ${!statusFilter ? theme.primary : '#E5E7EB'}`, background: !statusFilter ? theme.primary : '#fff', color: !statusFilter ? '#fff' : '#6B7280', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
          All ({projects.length})
        </button>
        {Object.entries(STATUS).map(([k, v]) => (
          <button key={k} onClick={() => setStatusFilter(statusFilter === k ? '' : k)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.875rem', borderRadius: 9999, border: `1.5px solid ${statusFilter === k ? v.dot : '#E5E7EB'}`, background: statusFilter === k ? v.bg : '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem', color: statusFilter === k ? v.text : '#6B7280' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: v.dot, flexShrink: 0 }} />{v.label} ({counts[k] || 0})
          </button>
        ))}
        <div style={{ position: 'relative', marginLeft: 'auto', minWidth: 220 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: '0.85rem', pointerEvents: 'none' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or code…"
            style={{ ...inp, paddingLeft: '2rem', width: '100%', boxSizing: 'border-box', fontSize: '0.82rem' }} />
        </div>
      </div>

      {error && <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', color: '#9F1239', padding: '0.75rem 1rem', borderRadius: 10, marginBottom: '1rem', fontSize: '0.875rem' }}>{error}</div>}

      {/* ── Table ── */}
      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid #E5E7EB`, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              {/* Column group label row */}
              <tr>
                <th colSpan={2} style={{ ...thBase, background: 'linear-gradient(90deg,#1E293B,#334155)', color: '#94A3B8', textAlign: 'center', cursor: 'default', fontSize: '0.62rem', borderRight: '2px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>PROJECT</th>
                <th colSpan={2} style={{ ...thBase, background: 'linear-gradient(90deg,#1D4ED8,#2563EB)', color: '#BFDBFE', textAlign: 'center', cursor: 'default', fontSize: '0.62rem', borderRight: '2px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>STATUS</th>
                <th colSpan={4} style={{ ...thBase, background: 'linear-gradient(90deg,#5B21B6,#7C3AED)', color: '#DDD6FE', textAlign: 'center', cursor: 'default', fontSize: '0.62rem', borderRight: '2px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>MILESTONES</th>
                <th colSpan={2} style={{ ...thBase, background: 'linear-gradient(90deg,#065F46,#059669)', color: '#A7F3D0', textAlign: 'center', cursor: 'default', fontSize: '0.62rem', borderRight: '2px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>PROGRESS</th>
                <th colSpan={3} style={{ ...thBase, background: 'linear-gradient(90deg,#92400E,#B45309)', color: '#FDE68A', textAlign: 'center', cursor: 'default', fontSize: '0.62rem', borderBottom: '1px solid rgba(255,255,255,0.08)', borderRight: '2px solid rgba(255,255,255,0.1)' }}>TIMELINE & MODE</th>
                {canDelete && <th colSpan={1} style={{ ...thBase, background: 'linear-gradient(90deg,#7F1D1D,#991B1B)', color: '#FECACA', textAlign: 'center', cursor: 'default', fontSize: '0.62rem', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>ACTIONS</th>}
              </tr>
              {/* Column name row */}
              <tr>
                <Th col="code"                   label="Project ID"      minW={100} bg="#1E293B" color="#CBD5E1" />
                <Th col="name"                   label="Project Name"    minW={170} bg="#1E293B" color="#CBD5E1" />
                <Th col="status"                 label="Status"          minW={110} bg="#1D4ED8" color="#BFDBFE" />
                <Th col="_track"                 label="Track"           minW={110} bg="#2563EB" color="#DBEAFE" />
                <Th col="total_milestones"       label="Milestones"      minW={100} bg="#5B21B6" color="#DDD6FE" center />
                <Th col="completed_milestones"   label="Completed"       minW={100} bg="#6D28D9" color="#EDE9FE" center />
                <Th col="current_milestone_name" label="Current MS"      minW={160} bg="#7C3AED" color="#F5F3FF" />
                <Th col="current_milestone_pct"  label="MS Progress"     minW={130} bg="#7C3AED" color="#F5F3FF" center />
                <Th col="completion_pct"         label="Overall Progress" minW={160} bg="#065F46" color="#A7F3D0" center />
                <Th col="_issues"                label="Issues Done"     minW={110} bg="#047857" color="#D1FAE5" center />
                <Th col="start_date"             label="Start Date"      minW={115} bg="#92400E" color="#FDE68A" />
                <Th col="end_date"               label="Planned End"     minW={115} bg="#B45309" color="#FEF3C7" />
                <Th col="mode"                   label="Mode"            minW={90}  bg="#B45309" color="#FEF3C7" />
                {canDelete && <th style={{ ...thBase, background: '#7F1D1D', color: '#FECACA', minWidth: 70, textAlign: 'center', cursor: 'default' }}>Delete</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canDelete ? 14 : 13} style={{ padding: '4rem', textAlign: 'center', color: '#9CA3AF' }}>
                  <div style={{ display: 'inline-block', width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: theme.primary, borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: 12 }} />
                  <div style={{ fontSize: '0.875rem' }}>Loading projects…</div>
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={canDelete ? 14 : 13} style={{ padding: '5rem', textAlign: 'center', color: '#9CA3AF' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>📋</div>
                  <p style={{ fontWeight: 700, color: theme.text, margin: '0 0 0.3rem' }}>No projects found</p>
                  <p style={{ fontSize: '0.8rem', margin: 0 }}>{search || statusFilter ? 'Try clearing your filters.' : 'Create your first project to get started.'}</p>
                  {canCreate && !search && !statusFilter && (
                    <button onClick={() => setShowModal(true)}
                      style={{ marginTop: '1.25rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: 10, padding: '0.6rem 1.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem' }}>
                      + Create Project
                    </button>
                  )}
                </td></tr>
              )}

              {!loading && filtered.map((p, idx) => {
                const s       = STATUS[p.status] || STATUS.active;
                const mo      = MODE[p.mode]     || MODE.kanban;
                const a       = accent(p.name);
                const tk      = trackStatus(p);
                const msTotal = p.total_milestones     || 0;
                const msDone  = p.completed_milestones || 0;
                const msPct   = p.current_milestone_pct || 0;
                const ovPct   = p.completion_pct || 0;

                const ovColor = ovPct >= 75 ? '#059669' : ovPct >= 40 ? '#2563EB' : ovPct >= 15 ? '#D97706' : '#DC2626';
                const msColor = msPct >= 75 ? '#059669' : msPct >= 40 ? '#7C3AED' : '#D97706';

                const isDelayed = ['Delayed', 'Behind'].includes(tk.label);
                const isAtRisk  = tk.label === 'At Risk';
                const isGood    = tk.label === 'On Track';

                const rowBorderColor = isDelayed ? '#FCA5A5' : isAtRisk ? '#FCD34D' : isGood ? '#6EE7B7' : '#E5E7EB';
                const rowBg = idx % 2 === 0 ? '#FFFFFF' : '#FAFBFF';

                return (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                    style={{ cursor: 'pointer', borderBottom: `1px solid #F1F5F9`, borderLeft: `4px solid ${rowBorderColor}`, background: rowBg, transition: 'background 0.12s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${a}0A`; e.currentTarget.style.borderLeftColor = a; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg;    e.currentTarget.style.borderLeftColor = rowBorderColor; }}>

                    {/* ── Project ID ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '1px solid #F3F4F6' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${a}15`, border: `1px solid ${a}40`, borderRadius: 7, padding: '3px 8px' }}>
                        {p.logo
                          ? <img src={getFileUrl(p.logo)} alt={p.code}
                              style={{ width: 16, height: 16, borderRadius: 3, objectFit: 'contain', flexShrink: 0 }} />
                          : <span style={{ width: 7, height: 7, borderRadius: '50%', background: a, flexShrink: 0 }} />
                        }
                        <span style={{ fontFamily: 'monospace', fontWeight: 800, color: a, fontSize: '0.8rem', letterSpacing: '0.04em' }}>{p.code}</span>
                      </div>
                    </td>

                    {/* ── Project Name ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '2px solid #E5E7EB' }}>
                      <div style={{ fontWeight: 700, color: '#111827', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{p.name}</div>
                      {p.description && (
                        <div style={{ fontSize: '0.7rem', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, marginTop: 2 }}>{p.description}</div>
                      )}
                    </td>

                    {/* ── Status ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '1px solid #F3F4F6' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.text, padding: '4px 10px', borderRadius: 9999, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />{s.label}
                      </span>
                    </td>

                    {/* ── Track Status ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '2px solid #E5E7EB' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: tk.bg, color: tk.color, border: `1.5px solid ${tk.border}`, padding: '4px 10px', borderRadius: 9999, fontSize: '0.72rem', fontWeight: 800, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
                        <span style={{ fontSize: '0.7rem' }}>{tk.icon}</span>{tk.label}
                      </span>
                    </td>

                    {/* ── Total Milestones ── */}
                    <td style={{ padding: '0.7rem 0.875rem', textAlign: 'center', borderRight: '1px solid #F3F4F6' }}>
                      {msTotal > 0
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#EDE9FE', color: '#6D28D9', fontWeight: 800, fontSize: '0.8rem' }}>{msTotal}</span>
                        : <span style={{ color: '#D1D5DB', fontSize: '0.8rem' }}>—</span>
                      }
                    </td>

                    {/* ── Completed Milestones ── */}
                    <td style={{ padding: '0.7rem 0.875rem', textAlign: 'center', borderRight: '1px solid #F3F4F6' }}>
                      {msTotal > 0
                        ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                            <span style={{ fontWeight: 800, color: msDone === msTotal ? '#059669' : '#374151', fontSize: '0.82rem' }}>{msDone}<span style={{ color: '#9CA3AF', fontWeight: 400 }}>/{msTotal}</span></span>
                            <div style={{ width: 40, background: '#E5E7EB', borderRadius: 999, height: 4, overflow: 'hidden' }}>
                              <div style={{ width: `${msTotal > 0 ? msDone / msTotal * 100 : 0}%`, height: '100%', background: msDone === msTotal ? '#059669' : '#8B5CF6', borderRadius: 999 }} />
                            </div>
                          </div>
                        : <span style={{ color: '#D1D5DB', fontSize: '0.8rem' }}>—</span>
                      }
                    </td>

                    {/* ── Current Milestone ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '1px solid #F3F4F6', maxWidth: 170 }}>
                      {p.current_milestone_name
                        ? <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8B5CF6', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#5B21B6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.current_milestone_name}</span>
                          </div>
                        : <span style={{ color: '#D1D5DB', fontSize: '0.75rem', fontStyle: 'italic' }}>No active MS</span>
                      }
                    </td>

                    {/* ── Milestone Progress ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '2px solid #E5E7EB' }}>
                      {p.current_milestone_name
                        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 110 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: msColor }}>{msPct}%</span>
                              <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>{msPct === 100 ? '✓ Done' : 'in progress'}</span>
                            </div>
                            <ProgressBar pct={msPct} color={msColor} trackered />
                          </div>
                        : <span style={{ color: '#D1D5DB', fontSize: '0.75rem' }}>—</span>
                      }
                    </td>

                    {/* ── Overall Progress ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: '1px solid #F3F4F6' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 900, color: ovColor }}>{ovPct}%</span>
                          {ovPct === 100 && <span style={{ fontSize: '0.65rem', background: '#D1FAE5', color: '#065F46', padding: '1px 6px', borderRadius: 9999, fontWeight: 700 }}>Done</span>}
                        </div>
                        <ProgressBar pct={ovPct} color={ovColor} trackered />
                      </div>
                    </td>

                    {/* ── Issues Done ── */}
                    <td style={{ padding: '0.7rem 0.875rem', textAlign: 'center', borderRight: '2px solid #E5E7EB' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#059669' }}>{p.completed_issues || 0}</span>
                        <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>of {p.total_issues || 0}</span>
                      </div>
                    </td>

                    {/* ── Start Date ── */}
                    <td style={{ padding: '0.7rem 0.875rem', whiteSpace: 'nowrap', borderRight: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: '0.78rem', color: '#6B7280' }}>{fmt(p.start_date)}</span>
                    </td>

                    {/* ── Planned End ── */}
                    <td style={{ padding: '0.7rem 0.875rem', whiteSpace: 'nowrap', borderRight: '1px solid #F3F4F6' }}>
                      {p.end_date
                        ? (() => {
                            const isOver = new Date(p.end_date) < new Date() && p.status !== 'completed';
                            const daysLeft = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
                            return (
                              <div>
                                <span style={{ fontSize: '0.78rem', fontWeight: isOver ? 700 : 400, color: isOver ? '#DC2626' : daysLeft <= 14 ? '#D97706' : '#6B7280' }}>
                                  {fmt(p.end_date)}
                                </span>
                                {!isOver && p.status === 'active' && daysLeft >= 0 && (
                                  <div style={{ fontSize: '0.63rem', color: daysLeft <= 7 ? '#DC2626' : daysLeft <= 14 ? '#D97706' : '#9CA3AF', marginTop: 1 }}>
                                    {daysLeft === 0 ? 'Due today!' : `${daysLeft}d left`}
                                  </div>
                                )}
                                {isOver && <div style={{ fontSize: '0.63rem', color: '#DC2626', fontWeight: 700, marginTop: 1 }}>Overdue</div>}
                              </div>
                            );
                          })()
                        : <span style={{ color: '#D1D5DB', fontSize: '0.78rem' }}>—</span>
                      }
                    </td>

                    {/* ── Mode ── */}
                    <td style={{ padding: '0.7rem 0.875rem', borderRight: canDelete ? '1px solid #F3F4F6' : undefined }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: mo.bg, color: mo.text, padding: '3px 8px', borderRadius: 6, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {mo.icon} {mo.label}
                      </span>
                    </td>

                    {/* ── Delete ── */}
                    {canDelete && (
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={e => handleDelete(e, p.id, p.name)}
                          title={`Delete ${p.name}`}
                          style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '0.3rem 0.6rem', cursor: 'pointer', color: '#DC2626', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1, transition: 'background 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#FEE2E2'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#FEF2F2'; }}>
                          🗑
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Footer ── */}
        {!loading && filtered.length > 0 && (
          <div style={{ padding: '0.7rem 1.25rem', borderTop: `1px solid #E5E7EB`, background: 'linear-gradient(90deg,#F8FAFC,#F0F4FF)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.775rem', color: '#6B7280' }}>
            <span>Showing <strong style={{ color: theme.text }}>{filtered.length}</strong> of <strong style={{ color: theme.text }}>{projects.length}</strong> projects</span>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <span>Total issues: <strong style={{ color: '#1D4ED8' }}>{projects.reduce((s, p) => s + (p.total_issues || 0), 0)}</strong></span>
              <span>Completed: <strong style={{ color: '#059669' }}>{projects.reduce((s, p) => s + (p.completed_issues || 0), 0)}</strong></span>
              <span>Avg progress: <strong style={{ color: theme.primary }}>{projects.length ? Math.round(projects.reduce((s, p) => s + (p.completion_pct || 0), 0) / projects.length) : 0}%</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* ── Create modal ── */}
      {showModal && (
        <Modal title="Create New Project" onClose={() => { setShowModal(false); setMilestones([]); }}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Field label="Project Name *">
              <input required value={form.name} onChange={e => {
                const name = e.target.value;
                const autoCode = name.trim().split(/\s+/).map(w => w[0] || '').join('').toUpperCase().slice(0, 6);
                setForm(f => ({ ...f, name, code: f.code || autoCode }));
              }} placeholder="e.g. BonTon Mobile App"
                style={{ ...inp, boxSizing: 'border-box', width: '100%' }} autoFocus />
            </Field>
            <Field label="Project Code *">
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. BMA" maxLength={10}
                style={{ ...inp, boxSizing: 'border-box', width: '100%', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }} />
            </Field>
            <Field label="Description">
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
                placeholder="Short description…" style={{ ...inp, resize: 'vertical', boxSizing: 'border-box', width: '100%', fontFamily: 'inherit' }} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
              <Field label="Execution Mode">
                <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))} style={{ ...inp, boxSizing: 'border-box', width: '100%', cursor: 'pointer' }}>
                  <option value="kanban">▦ Kanban</option>
                  <option value="scrum">⟳ Scrum</option>
                </select>
              </Field>
              <Field label="Initial Status">
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={{ ...inp, boxSizing: 'border-box', width: '100%', cursor: 'pointer' }}>
                  <option value="planned">Planned</option>
                  <option value="active">Active</option>
                </select>
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
              <Field label="Start Date">
                <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={{ ...inp, boxSizing: 'border-box', width: '100%' }} />
              </Field>
              <Field label="Planned End Date">
                <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={{ ...inp, boxSizing: 'border-box', width: '100%' }} />
              </Field>
            </div>
            {/* Financial fields */}
            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '0.875rem' }}>
              <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 0.75rem' }}>Financial & Timeline</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                <Field label="Total Project Value (₹)">
                  <input type="number" min="0" step="0.01" value={form.total_value} onChange={e => setForm(f => ({ ...f, total_value: e.target.value }))}
                    placeholder="e.g. 500000" style={{ ...inp, boxSizing: 'border-box', width: '100%' }} />
                </Field>
                <Field label="Payment Received (%)">
                  <input type="number" min="0" max="100" step="0.1" value={form.payment_percentage} onChange={e => setForm(f => ({ ...f, payment_percentage: e.target.value }))}
                    placeholder="0–100" style={{ ...inp, boxSizing: 'border-box', width: '100%' }} />
                </Field>
              </div>
            </div>
            {/* Milestones Section */}
            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>
                    Milestones <span style={{ color: '#DC2626' }}>*</span>
                  </p>
                  <p style={{ fontSize: '0.7rem', margin: '2px 0 0', color: '#9CA3AF' }}>Total payment % across all milestones must equal 100%</p>
                </div>
                <button type="button" onClick={addMilestone}
                  style={{ padding: '0.35rem 0.85rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
                  + Add Milestone
                </button>
              </div>

              {milestones.map((ms, i) => {
                const pctVal = parseFloat(ms.payment_percentage) || 0;
                const calcAmt = form.total_value && pctVal ? (parseFloat(form.total_value) * pctVal / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : null;
                return (
                  <div key={i} style={{ background: '#F8FAFC', border: `1.5px solid ${theme.border}`, borderRadius: 8, padding: '0.75rem', marginBottom: '0.625rem', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 800, color: theme.primary, background: '#EEF2FF', padding: '0.15rem 0.55rem', borderRadius: 9999, flexShrink: 0 }}>M-{i + 1}</span>
                      <input
                        value={ms.name}
                        onChange={e => updateMilestone(i, 'name', e.target.value)}
                        placeholder="Milestone name (e.g. Requirements Sign-off)"
                        style={{ ...inp, flex: 1, boxSizing: 'border-box', fontSize: '0.82rem', marginBottom: 0, fontWeight: 600 }}
                      />
                      <button type="button" onClick={() => removeMilestone(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }}>✕</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                      <Field label="Planned Start Date">
                        <input type="date" value={ms.planned_start_date}
                          onChange={e => updateMilestone(i, 'planned_start_date', e.target.value)}
                          style={{ ...inp, boxSizing: 'border-box', width: '100%', fontSize: '0.8rem' }} />
                      </Field>
                      <Field label="Planned End Date">
                        <input type="date" value={ms.planned_end_date}
                          onChange={e => updateMilestone(i, 'planned_end_date', e.target.value)}
                          style={{ ...inp, boxSizing: 'border-box', width: '100%', fontSize: '0.8rem' }} />
                      </Field>
                      <Field label="Payment %">
                        <input type="number" min="0" max="100" step="0.1" value={ms.payment_percentage}
                          onChange={e => updateMilestone(i, 'payment_percentage', e.target.value)}
                          placeholder="e.g. 25"
                          style={{ ...inp, boxSizing: 'border-box', width: '100%', fontSize: '0.8rem', borderColor: pctVal > 0 ? theme.primary : theme.border }} />
                      </Field>
                    </div>
                    {calcAmt && (
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#5B21B6', fontWeight: 600 }}>
                        ₹ {calcAmt} for this milestone
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Payment % summary + warning */}
              {milestones.length > 0 && (() => {
                const remaining   = parseFloat((100 - totalPct).toFixed(2));
                const remainAmt   = form.total_value && remaining > 0
                  ? (parseFloat(form.total_value) * remaining / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })
                  : null;
                const isExact     = Math.round(totalPct * 10) === 1000;
                const isOver      = totalPct > 100;
                const bgColor     = isExact ? '#DCFCE7' : isOver ? '#FEE2E2' : '#FEF9C3';
                const borderColor = isExact ? '#86EFAC' : isOver ? '#FECACA' : '#FDE047';
                const textColor   = isExact ? '#166534' : isOver ? '#991B1B' : '#92400E';
                return (
                  <div style={{ background: bgColor, border: `1.5px solid ${borderColor}`, borderRadius: 8, padding: '0.75rem 1rem', marginTop: '0.25rem' }}>
                    {/* Progress bar */}
                    <div style={{ height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 9999, overflow: 'hidden', marginBottom: '0.5rem' }}>
                      <div style={{ height: '100%', width: `${Math.min(totalPct, 100)}%`, background: isExact ? '#16A34A' : isOver ? '#DC2626' : '#D97706', borderRadius: 9999, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 800, color: textColor }}>
                          {isExact && '✓ All 100% allocated across milestones'}
                          {isOver  && `⚠ Exceeds 100% — reduce by ${(totalPct - 100).toFixed(1)}%`}
                          {!isExact && !isOver && `⚠ ${remaining}% not mapped to any milestone`}
                        </p>
                        {!isExact && !isOver && remainAmt && (
                          <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: textColor, fontWeight: 600 }}>
                            ₹ {remainAmt} is unallocated — assign to a milestone before creating
                          </p>
                        )}
                      </div>
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: textColor, flexShrink: 0, marginLeft: '1rem' }}>
                        {totalPct.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })()}

              {milestones.length === 0 && (
                <div style={{ background: '#FFF7ED', border: '1.5px dashed #FED7AA', borderRadius: 8, padding: '0.875rem', textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400E', fontWeight: 600 }}>Add at least one milestone with 100% payment allocation to create the project</p>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '0.5rem', borderTop: `1px solid ${theme.border}`, marginTop: '0.25rem' }}>
              <button type="button" onClick={() => { setShowModal(false); setMilestones([]); }}
                style={{ padding: '0.55rem 1.1rem', border: `1.5px solid ${theme.border}`, background: '#fff', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>
                Cancel
              </button>
              {(() => {
                const isReady = milestones.length > 0 && Math.round(totalPct * 10) === 1000 && !saving;
                return (
                  <button type="submit" disabled={!isReady}
                    style={{ padding: '0.55rem 1.4rem', background: isReady ? `linear-gradient(135deg,${theme.primary},#6366F1)` : '#D1D5DB', color: '#fff', border: 'none', borderRadius: 10, cursor: isReady ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: '0.875rem', boxShadow: isReady ? `0 4px 14px ${theme.primary}40` : 'none', transition: 'all 0.2s' }}>
                    {saving ? 'Creating…' : milestones.length === 0 ? 'Add Milestones First' : Math.round(totalPct * 10) !== 1000 ? `${(100 - totalPct).toFixed(1)}% Unallocated` : '✓ Create Project'}
                  </button>
                );
              })()}
            </div>
          </form>
        </Modal>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        tr:hover td { transition: background 0.1s; }
      `}</style>
    </div>
  );
}
